import { del, get, patch, post } from '@/shared/utils/api-utils'
import { createSingleFileFormData } from '@/shared/utils/file-upload-utils'
import { OrgSettings, SwitchOrgRequest } from '@/store/org/types'
import {
  EquipmentCatalogProfile,
  InsertOrg,
  Organization,
} from '@depanneo/types'

/**
 * A DB row as a request body, with the one difference that matters: the
 * `decimal` columns are read back as strings but SENT as numbers (the DTOs
 * validate numbers; the API stringifies them before the write).
 */
type DecimalColumns =
  | 'latitude'
  | 'longitude'
  | 'interventionTriggerLimit'
  | 'mandatoryDiscountPercent'
export type OrgPayload = Omit<InsertOrg, DecimalColumns> & {
  [K in DecimalColumns]?: number | null
}

// `equipmentCatalogProfile` is a transient provisioning field accepted by the
// create endpoint — it is NOT an organizations column, so it is not part of
// InsertOrg. Widen the create payload rather than touching the DB type.
export type CreateOrgPayload = OrgPayload & {
  equipmentCatalogProfile?: EquipmentCatalogProfile
}

/**
 * The created row, plus how the requested catalog import went (#2762). The
 * org exists either way; `failed` means it has no catalog yet.
 */
export type CreatedOrganization = Organization & {
  equipmentCatalog?: {
    catalog: EquipmentCatalogProfile | 'standard'
    status: 'imported' | 'failed'
  }
}

const createOrg = async (data: CreateOrgPayload) => {
  const response = await post<CreatedOrganization, CreateOrgPayload>(
    '/organizations',
    data
  )
  return response
}

/** `importCatalogProfileIntoOrgInTx`'s report, as the API answers it. */
export interface EquipmentCatalogImportReport {
  profile: EquipmentCatalogProfile
  revision: string
  inserted: number
  updated: number
  unchanged: number
  retired: number
}

/**
 * SUPER_ADMIN only. Installs a B2B / B2C catalog into the organization named
 * here — not the active one. Re-running it is safe: ids are kept.
 */
const importEquipmentCatalog = async ({
  organizationId,
  profile,
}: {
  organizationId: string
  profile: EquipmentCatalogProfile
}) => {
  const response = await post<
    EquipmentCatalogImportReport,
    { profile: EquipmentCatalogProfile }
  >(`/organizations/${organizationId}/equipment/import-standard`, { profile })
  return response
}

const findAllOrgs = async () => {
  const response = await get<Organization[]>('/organizations')
  return response
}

/** SUPER_ADMIN only: the API ignores `includeInactive` for every other role. */
const findAllOrgsIncludingInactive = async () => {
  const response = await get<Organization[]>('/organizations', {
    params: { includeInactive: true },
  })
  return response
}

/** SUPER_ADMIN only (#2673): hides the org and locks its members out, or undoes it. */
const setOrgActive = async ({
  id,
  active,
}: {
  id: string
  active: boolean
}) => {
  const response = await patch<Organization, { active: boolean }>(
    `/organizations/${id}/active`,
    { active }
  )
  return response
}

/**
 * The org row, plus the two fields the API resolves ALONGSIDE it.
 *
 * `quoteAutoDecisionEnabled` is not org data at all — it is the
 * `QUOTE_AUTO_DECISION_ENABLED` kill switch for this environment (#1586), and
 * the settings screen needs it so the automation controls can say they are
 * inert instead of silently saving into a feature nobody turned on.
 */
export type OrganizationWithResolvedFields = Organization & {
  logoUrl: string | null
  quoteAutoDecisionEnabled?: boolean
}

const findOrg = async (id: string) => {
  const response = await get<OrganizationWithResolvedFields>(
    `/organizations/${id}`
  )
  return response
}

const updateOrg = async (id: string, data: Partial<OrgPayload>) => {
  const response = await patch<Organization, Partial<OrgPayload>>(
    `/organizations/${id}`,
    data
  )
  return response
}

const updateOrgSettings = async (id: string, data: OrgSettings) => {
  const response = await patch<Organization, OrgSettings>(
    `/organizations/${id}/settings`,
    data
  )
  return response
}

const deleteOrg = async (id: string) => {
  const response = await del<Organization>(`/organizations/${id}`)
  return response
}

// Returns the FULL target org row (`OrganizationsService.switch`), which is what
// the active-org store stores — the old `Pick<Organization, 'id'>` under-declared
// it and made every caller look like it only had an id.
const switchOrg = async (id: string, signal?: AbortSignal) => {
  const response = await post<Organization, SwitchOrgRequest>(
    `/organizations/switch`,
    { targetOrgId: id },
    { signal }
  )
  return response
}

const uploadLogo = async (id: string, file: File) => {
  const formData = createSingleFileFormData(file)
  return await post<Organization, FormData>(
    `/organizations/${id}/logo`,
    formData
  )
}

const deleteLogo = async (id: string, logoDocumentId: string) => {
  const response = await del<Organization>(
    `/organizations/${id}/logo/${logoDocumentId}`
  )
  return response
}

export {
  createOrg,
  importEquipmentCatalog,
  findAllOrgs,
  findAllOrgsIncludingInactive,
  setOrgActive,
  findOrg,
  updateOrg,
  updateOrgSettings,
  deleteOrg,
  switchOrg,
  uploadLogo,
  deleteLogo,
}
